import { cn } from '@/lib/utils';

interface SegmentChipProps {
  label: string;
  value: string;
  sub?: string;
  active: boolean;
  accent: 'primary' | 'ok' | 'danger' | 'warn' | 'info' | 'accent';
  onClick: () => void;
  /**
   * Informational-only chips (the Avg chip) highlight on click but don't
   * change the result set. Consumers handle the no-op; this prop is purely
   * for affordance (cursor, title).
   */
  informational?: boolean;
  title?: string;
}

const ACCENT_BAR: Record<SegmentChipProps['accent'], string> = {
  primary: 'bg-primary',
  ok: 'bg-[hsl(142_71%_45%)]',
  danger: 'bg-destructive',
  warn: 'bg-[hsl(38_92%_50%)]',
  info: 'bg-[hsl(213_94%_60%)]',
  accent: 'bg-[hsl(262_83%_58%)]'
};

export function SegmentChip({
  label,
  value,
  sub,
  active,
  accent,
  onClick,
  informational,
  title
}: SegmentChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        'group relative flex min-w-0 flex-col gap-0.5 rounded-lg px-3 py-2.5 text-left transition-all',
        'border border-transparent',
        active
          ? 'bg-background border-border shadow-sm text-foreground'
          : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
        informational && 'cursor-default'
      )}
      aria-pressed={active}
    >
      {active && (
        <span
          aria-hidden
          className={cn(
            'absolute inset-x-3 top-0 h-0.5 rounded-b',
            ACCENT_BAR[accent]
          )}
        />
      )}
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="break-words font-mono text-lg font-semibold leading-tight tracking-tight text-foreground">
        {value}
      </span>
      {sub && (
        <span className="break-words text-[11px] text-muted-foreground">{sub}</span>
      )}
    </button>
  );
}
